import React, { useEffect, useState, useMemo, useContext } from 'react';

import { CollectionWrapper } from '@src/meta/types/api';
import { alertError } from '@src/lib/alert';
import SentryUtils from '@src/lib/sentry';
import { AccountInfo } from '@modules/auth/components/types/user/index';
import { Maintenance } from '@src/components/Maintenance';
import Tabs from '@modules/common/components/Tabs';

import ClientResource from '../../../components/services/ClientResource';
import {
  OAuthClientDescriptor,
  ServerOAuthClientDescriptor,
  mapClients,
} from '../../../components/types/oauth-client';
import ClientsList from './ClientsList';
import NewClient from '../NewClient';
import Header from './Header';
import { AuthenticationContext } from '../../../components/context';

type Props = {
  account: AccountInfo;
};

function ExistingClients({ account }: Props) {
  const [fetchingClients, setFetchingClients] = useState<boolean>(true);
  const [clients, setClients] = useState<OAuthClientDescriptor[]>([]);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setFetchingClients(true);
    try {
      const response: CollectionWrapper<ServerOAuthClientDescriptor> = await ClientResource.search({
        accountId: account.id,
      });
      setClients(mapClients(response.member));
    } catch (error) {
      const errorMessage = 'Something went wrong while fetching OAuthClientDescriptor';
      alertError(errorMessage);
      SentryUtils.captureAPIError(error, errorMessage);
    }
    setFetchingClients(false);
  };

  const { activeClients, inactiveClients } = useMemo(() => {
    const activeClients = clients.filter(client => !client.isDeleted);
    activeClients.sort((a, b) => (a.name > b.name ? 1 : -1));
    return {
      activeClients,
      inactiveClients: clients.filter(client => client.isDeleted),
    };
  }, [clients]);

  const tabs = useMemo(
    () => [
      {
        title: 'Active',
        content: <ClientsList editable clients={activeClients} onClientUpdated={fetchClients} />,
      },
      {
        title: 'Inactive',
        content: <ClientsList clients={inactiveClients} />,
      },
    ],
    [activeClients, inactiveClients, fetchClients],
  );

  const { readonly, maintenanceInProgress } = useContext(AuthenticationContext);
  return (
    <>
      <Header />
      {maintenanceInProgress && <Maintenance service="managing OAuth2 clients" />}
      {!readonly && (
        <NewClient
          onClientCreated={fetchClients}
          activeClientsCount={activeClients.length}
          account={account}
        />
      )}
      <Tabs loading={fetchingClients} tabs={tabs} />
    </>
  );
}

export default ExistingClients;
